import type { FC, MouseEvent, PropsWithChildren } from 'react';
import { memo, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { twMerge } from 'tailwind-merge';

import { useBlockedUsers } from '../../../graphdebate/src/hooks/useBlockedUsers';
import type { AvatarPropsType } from '../components/Avatar';
import { Link } from '../components/Link';
import type { RequestFriendCallback } from '../contributor/api/relation';
import { GradeBadge } from '../contributor/Profile/ProfileData/GradeBadge';
import type { ProfilePopoverProps } from '../contributor/Profile/ProfilePopover';
import { ProfilePopover } from '../contributor/Profile/ProfilePopover';
import { useIsMobile } from '../hooks/useIsMobile';
import { blockedMessages } from '../i18n/blocked';
import type { Author, Profile } from '../types/api';
import './AuthorPseudo.scss';
import { getAuthorPseudo } from './thread-utils';

const messages = defineMessages({
  nonAuthUser: {
    defaultMessage: 'Anonymous',
    id: 'LXxsbk',
  },
});

type AuthorPseudoProps = {
  author: Author | undefined;
  userId: string | undefined;
  profile: Profile | undefined;
  pseudoClassName?: string;
  withProfilePopover?: boolean;
  /**
   * B8_05 (EEAT/SEO) : rend le pseudo comme un vrai lien crawlable `<a href="/p/<pseudo>">`
   * (au lieu d'un simple `<span>` navigué en JS). Opt-in réservé aux bylines de fil
   * (D41 = « signature du fil seulement ») — les listes/membres/relations restent des `<span>`.
   * Combiné au popover (D40=B), le clic garde l'aperçu (navigation neutralisée) : l'`<a>`
   * n'existe que pour le crawler / les bots IA (cohérence avec le `author.url` du JSON-LD).
   */
  withProfileLink?: boolean;
  withStatusBadge?: boolean;
  inviteCallback?: RequestFriendCallback;
  isGroup?: boolean;
} & Omit<AvatarPropsType, 'picture'> &
  Omit<Partial<ProfilePopoverProps>, 'userId'>;

export const AuthorPseudo: FC<AuthorPseudoProps> = memo(function AuthorPseudo(props) {
  const {
    author,
    userId,
    profile,
    withProfilePopover,
    withProfileLink,
    withStatusBadge,
    inviteCallback,
    isGroup,
    pseudoClassName,
    ...others
  } = props;

  const isMobileDisplay = useIsMobile();
  const { formatMessage } = useIntl();
  const { isBlocked, isNative } = useBlockedUsers();

  // Native blocking: return blocked label for blocked authors on native
  const isAuthorBlocked = isNative && isBlocked(userId);

  // D40=B : à l'intérieur du popover, le clic sur le lien ne navigue pas — il laisse
  // le popover d'aperçu s'ouvrir (comportement inchangé). L'`<a href>` reste dans le DOM
  // (SSR) pour le crawler. Hors popover (withProfilePopover=false), le lien navigue normalement.
  const preventNavWhenPopover = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      if (withProfilePopover && !isAuthorBlocked) e.preventDefault();
    },
    [withProfilePopover, isAuthorBlocked],
  );

  if (!profile) {
    return (
      <PseudoDisplay pseudoClassName={pseudoClassName} isGroup={isGroup}>
        <FormattedMessage {...messages.nonAuthUser} />
      </PseudoDisplay>
    );
  }

  const pseudo = isAuthorBlocked
    ? formatMessage(blockedMessages.blockedUserLabel)
    : getAuthorPseudo(author, profile);

  const pseudoDisplay = (
    <PseudoDisplay pseudoClassName={pseudoClassName} isGroup={isGroup}>
      {pseudo}
    </PseudoDisplay>
  );

  // Byline crawlable (D41 opt-in) : seulement si un slug de profil existe (sinon fallback span).
  // Le pseudo bloqué n'est jamais un lien (pas de profil à exposer).
  const displayedPseudo =
    withProfileLink && !isAuthorBlocked && profile.pseudo ? (
      <Link to="profile" params={{ pseudo: profile.pseudo }}>
        <a className="AuthorPseudo_link" onClick={preventNavWhenPopover}>
          {pseudoDisplay}
        </a>
      </Link>
    ) : (
      pseudoDisplay
    );

  return (
    // Wrap in a span to avoid a tippy warning
    <span
      className={twMerge(
        'AuthorPseudo flex items-center gap-x-2 overflow-hidden text-gray-graphite',
      )}
    >
      {/* Native blocking: no popover when author is blocked */}
      {withProfilePopover && !isAuthorBlocked ? (
        <ProfilePopover
          userId={userId}
          trigger={isMobileDisplay ? 'click' : 'mouseenter'}
          appendToBody
          inviteCallback={inviteCallback}
          {...others}
        >
          {displayedPseudo}
        </ProfilePopover>
      ) : (
        displayedPseudo
      )}
      {withStatusBadge && <GradeBadge profile={profile} />}
    </span>
  );
});

const PseudoDisplay = ({
  isGroup,
  pseudoClassName,
  children,
  ...rest
}: PropsWithChildren<Pick<AuthorPseudoProps, 'pseudoClassName' | 'isGroup'>>) => {
  return (
    <span
      className={twMerge(
        'PseudoDisplay font-semibold cursor-pointer truncate leading-[20px]',
        isGroup ? 'text-base2' : 'text-[14px]',
        pseudoClassName,
      )}
      {...rest} // needed for tippy to work
    >
      {children}
    </span>
  );
};
